import { type ReactNode, useMemo } from "react";
import type { GameDefinition, GameId } from "modules/common/model/models";
import {
  registeredCardFacets,
  type FacetContext,
  type RegisteredCardFacet,
} from "modules/common/cardFacets";
import {
  ManaColorIcon,
  ManaFontTypeSymbols,
} from "modules/common/components/ManaFontSymbols";
import {
  CharacterIcon,
  DonIcon,
  EventIcon,
  LeaderIcon,
  StageIcon,
} from "modules/common/components/Icons/OnePieceTypeIcons";
import { useGameRegistry } from "modules/common/gameRegistryContext";
import { getGameMetadata } from "modules/common/gameRegistry";

export const CONDITION_LABELS: Record<number, string> = {
  1: "Poor",
  2: "Played",
  3: "Light Played",
  4: "Good",
  5: "Excellent",
  6: "Near Mint",
  7: "Mint",
};

export function formatCondition(value: string | number): string {
  return CONDITION_LABELS[Number(value)] ?? String(value);
}

export function formatFacetLabel(
  value: string | number | null | undefined,
  formatLabel?: (value: string | number) => string
): string {
  if (value === "" || value === null || value === undefined) return "None";
  return formatLabel ? formatLabel(value) : String(value);
}

const COLOR_CODE_TO_NAME: Record<string, string> = {
  W: "White",
  U: "Blue",
  B: "Black",
  R: "Red",
  G: "Green",
  C: "Colorless",
};

export function formatColor(value: string | number): string {
  return COLOR_CODE_TO_NAME[String(value)] ?? String(value);
}

const DOMAIN_COLORS: Record<string, string> = {
  Mind: "#5bbcd6",
  Fury: "#e85d3a",
  Calm: "#6dc4a0",
  Order: "#f2d16b",
  Chaos: "#c77dba",
  Body: "#d4956a",
};

function renderDomainIcon(value: string | number): ReactNode {
  const color = DOMAIN_COLORS[String(value)];
  if (!color) return null;
  return (
    <span
      className="inline-block h-3 w-3 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

function renderTypeIcon(value: string | number): ReactNode {
  const type = String(value);
  const icons: Record<string, ReactNode> = {
    Leader: <LeaderIcon size="small" />,
    Character: <CharacterIcon size="small" />,
    Event: <EventIcon size="small" />,
    Stage: <StageIcon size="small" />,
    "DON!!": <DonIcon size="small" />,
  };
  return icons[type] ?? <ManaFontTypeSymbols type={type} className="text-xs" />;
}

export interface FacetDefinition {
  key: string;
  title: string;
  component: RegisteredCardFacet["control"];
  facetKey: string;
  filterKey: string;
  matchModeKey?: string;
  formatLabel?: (value: string | number) => string;
  renderIcon?: (value: string | number, game?: GameId) => ReactNode;
  gameExclusive?: GameId;
  minFilterKey?: string;
  maxFilterKey?: string;
  step?: number;
  isCurrency?: boolean;
  requiresMultipleGames?: boolean;
  exclusiveValue?: string | number;
}

function presentation(
  registration: RegisteredCardFacet
): Pick<FacetDefinition, "formatLabel" | "renderIcon"> {
  switch (registration.presentation) {
    case "condition":
      return { formatLabel: formatCondition };
    case "mana-color":
      return {
        formatLabel: formatColor,
        renderIcon: (value) => <ManaColorIcon color={String(value)} />,
      };
    case "domain":
      return { renderIcon: renderDomainIcon };
    case "card-type":
      return {
        renderIcon: (value, game) =>
          game && getGameMetadata(game).presentation.facetIcons.cardType
            ? renderTypeIcon(value)
            : null,
      };
    default:
      return {};
  }
}

function toFacetDefinition(registration: RegisteredCardFacet): FacetDefinition {
  return {
    key: registration.key,
    title: registration.title,
    component: registration.control,
    facetKey: registration.facetKey,
    filterKey:
      registration.selection?.key ??
      registration.range?.min.key ??
      registration.key,
    matchModeKey: registration.matchMode?.key,
    gameExclusive: registration.gameExclusive,
    minFilterKey: registration.range?.min.key,
    maxFilterKey: registration.range?.max.key,
    step: registration.range?.step,
    isCurrency: registration.range?.isCurrency,
    requiresMultipleGames: registration.requiresMultipleGames,
    exclusiveValue: registration.exclusiveValue,
    ...presentation(registration),
  };
}

export function facetDefinitionsForContext(
  context: FacetContext,
  contract: Parameters<typeof registeredCardFacets>[0]
): FacetDefinition[] {
  return registeredCardFacets(contract, context).map(toFacetDefinition);
}

export function useFacetDefinitions(context: FacetContext): FacetDefinition[] {
  const { definitions, sharedFacets } = useGameRegistry();
  const capability = context === "trade" ? "trading" : "catalogue";
  return useMemo(
    () =>
      facetDefinitionsForContext(context, {
        definitions: definitions.filter(
          (definition) => definition.capabilities[capability]
        ),
        sharedFacets,
      }),
    [capability, context, definitions, sharedFacets]
  );
}
